import { EFinancingProgram } from '@if/core/types'
import { FullCreditApplication, NormsMessage } from '@src/data/types'
import { AutoRslaWorksheet, EditAutoRslaWorksheetDto } from '@src/data/types/AutoRslaWorksheet'
import { ParameterizedMessage } from '@src/data/types/ParameterizedMessage'
import { getApiClient } from '@src/services/api-client'
import translate from '@src/services/translate'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'

const baseUrl = '/Applications/auto-rsla/{creditApplicationId}/Worksheet'

function buildUrl(creditApplicationId: string): string {
  return baseUrl.replace('{creditApplicationId}', creditApplicationId)
}

// API Functions
export async function getAutoRslaWorksheet(creditApplicationId: string): Promise<AutoRslaWorksheet> {
  const apiClient = getApiClient()
  const response = await apiClient.get<AutoRslaWorksheet>(buildUrl(creditApplicationId))
  return response.data
}

export async function createAutoRslaWorksheet(data: EditAutoRslaWorksheetDto): Promise<AutoRslaWorksheet> {
  const apiClient = getApiClient()
  const url = buildUrl(data.creditApplicationId ?? data.id)
  const response = await apiClient.post<AutoRslaWorksheet>(url, data)
  return response.data
}

export async function updateAutoRslaWorksheet(data: EditAutoRslaWorksheetDto): Promise<AutoRslaWorksheet> {
  const apiClient = getApiClient()
  const url = buildUrl(data.creditApplicationId ?? data.id)
  const response = await apiClient.put<AutoRslaWorksheet>(url, data)
  return response.data
}

export const autoRslaWorksheetKeys = {
  all: ['autoRslaWorksheet'] as const,
  detail: (creditApplicationId: string) => [...autoRslaWorksheetKeys.all, creditApplicationId] as const,
}

export function useGetAutoRslaWorksheet(creditApplicationId: string | undefined) {
  return useQuery({
    queryKey: autoRslaWorksheetKeys.detail(creditApplicationId ?? ''),
    queryFn: () => getAutoRslaWorksheet(creditApplicationId!),
    enabled: !!creditApplicationId,
  })
}

export function useCreateAutoRslaWorksheet(): [MutationFunction<AutoRslaWorksheet, EditAutoRslaWorksheetDto>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: createAutoRslaWorksheet,
    onSuccess: (createdWorksheet) => {
      const creditAppId = createdWorksheet.creditApplicationId ?? createdWorksheet.id
      // Update the worksheet-specific query
      queryClient.setQueryData(autoRslaWorksheetKeys.detail(creditAppId), createdWorksheet)
      // Update the full credit application query
      const queryKey = fullCreditApplicationKeys.detail(creditAppId, EFinancingProgram.AutoRsla)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: createdWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useUpdateAutoRslaWorksheet(): [MutationFunction<AutoRslaWorksheet, EditAutoRslaWorksheetDto>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateAutoRslaWorksheet,
    onSuccess: (updatedWorksheet) => {
      const creditAppId = updatedWorksheet.creditApplicationId ?? updatedWorksheet.id
      // Update the worksheet-specific query
      queryClient.setQueryData(autoRslaWorksheetKeys.detail(creditAppId), updatedWorksheet)
      // Update the full credit application query
      const queryKey = fullCreditApplicationKeys.detail(creditAppId, EFinancingProgram.AutoRsla)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

// Utility functions for derived data (previously in selectors)
function getParameterizedNormMessage(message: ParameterizedMessage): string {
  const translatedMessage = translate([`worksheet.norms.${message.id}`])

  let messageSuffix = ' | '
  message.parameters.forEach((p, i) => {
    if (i >= 1) messageSuffix = messageSuffix.concat(', ')

    const suffix = translate(`worksheet.paramNames.${p.name}`, { val: p.value })
    messageSuffix = messageSuffix.concat(suffix)
  })

  return translatedMessage.concat(messageSuffix)
}

export function getWorksheetNormWarningMessages(worksheet: AutoRslaWorksheet | null | undefined): NormsMessage[] {
  const worksheetNormMessages: NormsMessage[] = []
  worksheet?.normsDecision?.warningMessages.forEach((wr) => {
    const builtMessage = getParameterizedNormMessage(wr)
    const worksheetNormMessage: NormsMessage = {
      id: wr.id,
      message: builtMessage,
      applicantType: null,
    }
    worksheetNormMessages.push(worksheetNormMessage)
  })
  return worksheetNormMessages
}

export function getWorksheetNormRefusalMessages(worksheet: AutoRslaWorksheet | null | undefined): NormsMessage[] {
  const worksheetNormMessages: NormsMessage[] = []
  worksheet?.normsDecision?.refusalMessages.forEach((wr) => {
    const builtMessage = getParameterizedNormMessage(wr)
    const worksheetNormMessage: NormsMessage = {
      id: wr.id,
      message: builtMessage,
      applicantType: null,
    }
    worksheetNormMessages.push(worksheetNormMessage)
  })
  return worksheetNormMessages
}

export function getWorksheetNormRefusalAndWarningMessages(
  worksheet: AutoRslaWorksheet | null | undefined,
): NormsMessage[] {
  const warningsAndRefusals = worksheet?.normsDecision?.warningMessages?.concat(
    worksheet?.normsDecision?.refusalMessages,
  )

  const worksheetNormMessages: NormsMessage[] = []
  warningsAndRefusals?.forEach((wr) => {
    const builtMessage = getParameterizedNormMessage(wr)
    const worksheetNormMessage: NormsMessage = {
      id: wr.id,
      message: builtMessage,
      applicantType: null,
    }
    worksheetNormMessages.push(worksheetNormMessage)
  })
  return worksheetNormMessages
}
